/**
 * StatusWorkflowTab — Tab 3 des Card-Detail-Modal (CARD-05, CARD-06, CARD-08).
 *
 * - Status-Dropdown: alle 7 Spalten via COLUMNS → PATCH /leads/:id { status }
 *   Backend setzt auto_workflow_active=false bei manueller Status-Aenderung (AUTO-10).
 * - Assigned-User-Dropdown: aus store.users → PATCH /leads/:id { assignedUserId }
 * - Auto-Workflow-Anzeige read-only ("Aktiv" oder "Gestoppt — manuell uebernommen")
 * - Soft-Delete-Button rot, mit window.confirm-Bestaetigung → DELETE /leads/:id
 *
 * CI-Farben: #E9FF57 Background + #1A1A1A Text auf Save-Button.
 * Loeschen-Button rot mit text-white — rot != gelb, das ist OK.
 */

import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { COLUMNS } from '../../lib/columns';
import { useLeadBoard } from '../../store/useLeadBoard';
import type { Lead, LeadStatus } from '../../types';

interface Props {
  lead: Lead;
  onSaved: (l: Lead) => void;
  onDeleted: () => void;
}

export default function StatusWorkflowTab({ lead, onSaved, onDeleted }: Props) {
  const updateLead = useLeadBoard((s) => s.updateLead);
  const deleteLead = useLeadBoard((s) => s.deleteLead);
  const users = useLeadBoard((s) => s.users);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStatusChange = async (newStatus: LeadStatus) => {
    if (newStatus === lead.status) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateLead(lead.id, { status: newStatus });
      onSaved(updated);
    } catch (e: any) {
      setError(e?.message ?? 'Status-Aenderung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleAssignChange = async (raw: string) => {
    const newAssignedUserId = raw === '' ? null : Number(raw);
    if (newAssignedUserId === lead.assignedUserId) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateLead(lead.id, {
        assignedUserId: newAssignedUserId,
      });
      onSaved(updated);
    } catch (e: any) {
      setError(e?.message ?? 'Zuweisung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const ok = window.confirm(
      `Lead "${lead.vereinName}" wirklich loeschen?\nDie Karte verschwindet aus dem Board.`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await deleteLead(lead.id);
      onDeleted();
    } catch (e: any) {
      setError(e?.message ?? 'Loeschen fehlgeschlagen');
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Status-Dropdown */}
      <section>
        <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
          Status
        </label>
        <select
          value={lead.status}
          onChange={(e) => handleStatusChange(e.target.value as LeadStatus)}
          disabled={busy}
          className="w-full border border-[#DAD9D9] rounded px-3 py-2 text-sm text-[#1A1A1A] bg-white disabled:opacity-50"
        >
          {COLUMNS.map((col) => (
            <option key={col.status} value={col.status}>
              {col.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Manuelles Aendern stoppt den Auto-Workflow.
        </p>
      </section>

      {/* Assigned-User-Dropdown */}
      <section>
        <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
          Zugewiesen an
        </label>
        <select
          value={lead.assignedUserId ?? ''}
          onChange={(e) => handleAssignChange(e.target.value)}
          disabled={busy}
          className="w-full border border-[#DAD9D9] rounded px-3 py-2 text-sm text-[#1A1A1A] bg-white disabled:opacity-50"
        >
          <option value="">Niemand</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name ?? u.email}
            </option>
          ))}
        </select>
      </section>

      {/* Auto-Workflow-Anzeige */}
      <section className="border-t border-[#DAD9D9] pt-4">
        <label className="block text-sm font-medium text-[#1A1A1A] mb-1">
          Auto-Workflow
        </label>
        {lead.autoWorkflowActive ? (
          <span className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
            Aktiv — Follow-up-Mails laufen automatisch
          </span>
        ) : (
          <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">
            Gestoppt — manuell uebernommen
          </span>
        )}
      </section>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {/* Soft-Delete */}
      <section className="border-t border-[#DAD9D9] pt-4">
        <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
          Lead loeschen
        </label>
        <p className="text-xs text-gray-500 mb-2">
          Soft-Delete: Karte verschwindet, kann von Admins wiederhergestellt werden.
        </p>
        <button
          type="button"
          onClick={handleDelete}
          disabled={busy}
          className="bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Lead loeschen
        </button>
      </section>
    </div>
  );
}
